import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { MenuSeparator, menuItemClass, Popover } from "@/components/ui/popover"
import { StatusDot, StatusGlyph } from "@/components/ui/status-dot"
import { withPlatformAwake } from "@/features/home/model"
import { sessionQuery } from "@/lib/queries"
import { homeQuery, membersQuery } from "@/lib/settings-api"
import { cn } from "@/lib/utils"
import { platformQuery } from "./platform-api"
import { parseLeases, parseManifest, parseUpdateStatus, type RunnerRow, runnersQuery } from "./runner-api"
import {
  agentsThinkingOn,
  andList,
  type PillGroups,
  pillGroups,
  pillState,
  possessive,
  servesChip,
  summaryLine,
} from "./runner-groups"
import { useRunnersLive } from "./use-runners-live"

/**
 * One of your machines needs you: a local connection signed out or failing, or its last self-update failed (the amber
 * triangle on the pill).
 */
export const needsAttention = (r: RunnerRow): boolean => attentionReason(r) !== null

/** Why one of your machines needs you, in words ("Claude logged out", "update the runner"), or null. */
export const attentionReason = (r: RunnerRow): string | null => {
  const bad = parseManifest(r.manifest)?.localConnections.find((c) => c.status !== "ok")
  if (bad) return `${bad.label} ${bad.status === "logged_out" ? "logged out" : "needs attention"}`
  return parseUpdateStatus(r.updateStatus)?.state === "failed" ? "update the runner" : null
}

/** "online", "busy" (it runs something now) or "away" (offline). */
export const machineStateWord = (r: RunnerRow): string =>
  r.status === "offline"
    ? "away"
    : parseLeases(r.leases).length > 0 || (r.activeRuns ?? 0) > 0
      ? "busy"
      : "online"

/**
 * Decision 0159 (jargon row 34): the machines pill is for whoever can act on machines: owners and admins, and a member
 * who paired one. A member without a machine sees none ("○ 0" meant nothing to her).
 */
export const showsMachinesPill = (
  role: string | undefined,
  rows: ReadonlyArray<Pick<RunnerRow, "ownerUserId">>,
  viewerId: string,
): boolean =>
  role === undefined || role === "owner" || role === "admin" || rows.some((r) => r.ownerUserId === viewerId)

/** Member ids → names, for "Becky's" and the popover rows. */
const useNames = (workspaceId: string): ReadonlyMap<string, string> => {
  const members = useQuery(membersQuery(workspaceId)).data?.members
  return useMemo(
    () => new Map((members ?? []).map((m) => [m.userId, m.user.name || m.user.email] as const)),
    [members],
  )
}

/**
 * The workspace bar's machines pill (wave E, spec §7.4; replaces the runner dots): "● 3 live", green when a machine you
 * can use is live, hollow grey when none, an amber triangle when one of yours needs attention. Its popover lists Yours ·
 * Shared with the team · Others' personal. The data is pushed (`useRunnersLive`), not polled.
 */
export function MachinesPill({
  workspaceId,
  role,
  className,
}: {
  workspaceId: string
  /** The viewer's workspace role: a member who owns no machine sees no pill (decision 0159, jargon row 34). */
  role?: string | undefined
  className?: string
}) {
  useRunnersLive(workspaceId)
  const runners = useQuery(runnersQuery(workspaceId))
  const viewerId = useQuery(sessionQuery).data?.user.id ?? ""
  const names = useNames(workspaceId)
  const [open, setOpen] = useState(false)
  // re-walk 2026-10-28 (0166 §1.1): the Platform Runner with a subscription starts on demand, so it counts as live (never
  // "No machines online" while it can run work)
  const platform = useQuery({ ...platformQuery(workspaceId), refetchInterval: false }).data
  const rows = withPlatformAwake(runners.data, platform) ?? []
  const state = pillState(rows, viewerId, needsAttention)
  const thinking = agentsThinkingOn(useQuery(homeQuery(workspaceId)).data)
  // phone: the glyph and the number only (the aria-label keeps the whole sentence)
  const short = rows.some((r) => r.status === "online") ? state.label.replace(/ live$/, "") : ""
  if (!showsMachinesPill(role, rows, viewerId)) return null
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title="Machines"
      align="end"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={runners.data ? state.aria : "Machines"}
          className={cn(
            "tap mono-data flex h-8 items-center gap-1.5 rounded-pill border border-line-control px-2.5 text-ink-2 hover:bg-fill hover:text-ink",
            className,
          )}
        >
          <PillGlyph tone={runners.data ? state.tone : "none"} />
          {runners.isPending ? (
            <span className="max-md:sr-only">machines…</span>
          ) : runners.isError && !runners.data ? (
            <span className="max-md:sr-only">machines unavailable</span>
          ) : rows.length === 0 ? (
            <>
              <span className="max-md:sr-only">No machines online</span>
            </>
          ) : (
            <>
              <span className="max-md:hidden">{state.label}</span>
              {short && <span className="md:hidden">{short}</span>}
            </>
          )}
        </button>
      )}
    >
      <MachinesMenu
        groups={pillGroups(rows, viewerId, names)}
        viewerId={viewerId}
        workspaceId={workspaceId}
        names={names}
        thinking={thinking}
        summary={rows.length > 0 ? summaryLine(rows, viewerId) : null}
        onChoose={() => setOpen(false)}
      />
    </Popover>
  )
}

export function PillGlyph({ tone }: { tone: "live" | "none" | "attention" }) {
  return tone === "attention" ? (
    <StatusGlyph state="risk" label="one of your machines needs attention" />
  ) : (
    <StatusGlyph state={tone === "live" ? "live" : "wait"} label={false} />
  )
}

/** The popover body (rendered on its own in tests). */
export function MachinesMenu({
  groups,
  viewerId,
  workspaceId,
  names,
  thinking,
  summary = null,
  onChoose,
}: {
  groups: PillGroups<RunnerRow>
  viewerId: string
  workspaceId: string
  names: ReadonlyMap<string, string>
  /** runnerId → the agents that think there ("Lea and Eda think here"). */
  thinking?: ReadonlyMap<string, ReadonlyArray<string>>
  /** "3 machines · 3 live · you can use 2", the popover's first line. */
  summary?: string | null
  onChoose?: () => void
}) {
  const first = (id: string) => (names.get(id) ?? "A teammate").trim().split(/\s+/)[0] ?? "A teammate"
  // Your rows say what needs you (amber), else online · busy · away and Just you / Shared with the team; a team row of
  // someone else's says whose it is. The agents that think on a machine are named under it.
  const row = (r: RunnerRow) => {
    const mine = r.ownerUserId === viewerId
    const reason = mine ? attentionReason(r) : null
    const agents = thinking?.get(r.runnerId) ?? []
    return (
      <Link
        key={r.runnerId}
        role="menuitem"
        to="/w/$workspaceId/settings/runners/$runnerId"
        params={{ workspaceId, runnerId: r.runnerId }}
        onClick={onChoose}
        className={cn(menuItemClass, "flex-wrap")}
      >
        <StatusDot state={r.status === "online" ? "online" : "offline"} label={false} />
        <span className={cn("mono-data min-w-0 flex-1 truncate", r.status === "offline" && "text-muted")}>
          {r.name}
        </span>
        <span className={cn("type-meta shrink-0", reason && "text-risk-text")}>
          {reason ??
            [
              machineStateWord(r),
              mine ? servesChip(r, viewerId, null) : possessive(first(r.ownerUserId)),
            ].join(" · ")}
        </span>
        {agents.length > 0 && (
          <span className="type-meta basis-full pl-5 text-muted">
            {andList(agents)} {agents.length === 1 ? "thinks" : "think"} here
          </span>
        )}
      </Link>
    )
  }
  const head = (text: string) => <p className="kicker px-2 pt-1.5 pb-0.5 text-muted">{text}</p>
  return (
    <>
      {summary && <p className="mono-data px-2 pt-1 pb-1.5 text-muted">{summary}</p>}
      {head("Yours")}
      {groups.yours.length > 0 ? (
        groups.yours.map(row)
      ) : (
        <p className="type-meta px-2 py-1">
          You haven't paired a machine.
          {groups.team.length > 0 ? " Your work runs on a team machine." : ""}
        </p>
      )}
      {groups.team.length > 0 && (
        <>
          {head("Shared with the team")}
          {groups.team.map(row)}
        </>
      )}
      {groups.others.length > 0 && (
        <>
          {head("Others' personal")}
          {groups.others.map((o) => (
            <p key={o.ownerId} className="type-secondary flex items-center gap-2 px-2 py-1 text-ink-2">
              <span className="min-w-0 flex-1 truncate">{o.name}</span>
              <span className="type-meta shrink-0">
                {o.runners.filter((r) => r.status === "online").length} of {o.runners.length} live
              </span>
            </p>
          ))}
        </>
      )}
      <MenuSeparator />
      <Link
        role="menuitem"
        to="/w/$workspaceId/settings/runners"
        params={{ workspaceId }}
        onClick={onChoose}
        className={menuItemClass}
      >
        All machines →
      </Link>
      {groups.yours.length === 0 && (
        <Link
          role="menuitem"
          to="/w/$workspaceId/settings/runners/new"
          params={{ workspaceId }}
          onClick={onChoose}
          className={menuItemClass}
        >
          Pair this Mac
        </Link>
      )}
    </>
  )
}
